import type { DecorationSlot } from "../types";

export type AccessoryDefinition = {
  label: string;
  atlas: string;
  source: readonly [number, number, number, number];
  height: number;
  lift: number;
  slot: DecorationSlot;
  anchor: "head" | "hero" | "card";
  width?: number;
  offsetX?: number;
  offsetY?: number;
  opacity?: number;
};

const asset = (name: string) => `${import.meta.env.BASE_URL}assets/generated/${name}`;
const cell = (column: number, row: number) =>
  [column * 362, row * 362, 362, 362] as const;

const wearable = asset("deco-atlas-kawaii-wearables-alpha-v1.png");
const cool = asset("deco-atlas-cool-accessories-alpha-v1.png");
const flat = asset("deco-atlas-flat-alpha-v1.png");
const sketch = asset("deco-atlas-handdrawn-alpha-v1.png");
const botanical = asset("deco-atlas-botanical-alpha-v1.png");
const gothic = asset("deco-atlas-gothic-pixel-alpha-v1.png");
const street = asset("deco-atlas-street-play-alpha-v1.png");
const fantasy = asset("deco-atlas-wa-fantasy-alpha-v1.png");
const fitted = asset("deco-atlas-wearable-fit-alpha-v1.png");
const effects = asset("deco-atlas-layer-effects-alpha-v1.png");
const insetCell = (column: number, row: number) =>
  [column * 362 + 5, row * 362 + 5, 352, 352] as const;

const head = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) => ({
  label,
  atlas,
  source,
  height: 0.39,
  lift: 0.68,
  slot: "head",
  anchor: "head",
  ...extra,
}) satisfies AccessoryDefinition;

const hat = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) =>
  head(label, atlas, source, { height: 0.43, lift: 0.58, ...extra });

const eyes = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) =>
  head(label, atlas, source, { height: 0.25, lift: 0.14, slot: "face", ...extra });

const above = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) =>
  head(label, atlas, source, { height: 0.34, lift: 1.02, ...extra });

const side = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) =>
  head(label, atlas, source, { height: 0.27, lift: 0.46, offsetX: -0.16, ...extra });

const effect = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) =>
  head(label, atlas, source, {
    height: 1.12,
    width: 0.5,
    lift: 0.5,
    slot: "behind",
    anchor: "hero",
    opacity: 0.86,
    ...extra,
  });

const cardEffect = (label: string, atlas: string, source: AccessoryDefinition["source"], extra: Partial<AccessoryDefinition> = {}) =>
  head(label, atlas, source, {
    height: 0.5,
    width: 0.94,
    lift: 0.5,
    slot: "card",
    anchor: "card",
    opacity: 0.52,
    ...extra,
  });

export const accessoryDefinitions = {
  cat: head("ねこみみ", wearable, [20, 30, 335, 315]),
  bunny: head("うさみみ", wearable, [370, 28, 335, 315], { height: 0.43, lift: 0.74 }),
  bear: head("くまみみ", wearable, [710, 45, 350, 290]),
  dog: head("たれみみ", wearable, [1060, 60, 370, 275]),
  white: head("白いけもみみ", wearable, [20, 390, 340, 310]),
  ram: head("ひつじ角", wearable, [350, 390, 360, 310]),
  demon: head("小悪魔ホーン", wearable, [700, 390, 360, 300]),
  halo: above("天使のハロー", wearable, [1050, 390, 380, 300]),
  crown: hat("クラウン", wearable, [20, 740, 340, 300], { height: 0.34, lift: 0.7 }),
  beret: hat("ベレー帽", wearable, [355, 735, 360, 315]),
  headphones: head("ヘッドホン", wearable, [700, 720, 370, 330], { height: 0.46, lift: 0.55 }),
  flowers: head("花冠", wearable, [1055, 730, 380, 315], { height: 0.43, lift: 0.62 }),

  pixelShades: eyes("ピクセルサングラス", street, cell(0, 0), { height: 0.21 }),
  snapback: hat("スナップバック", street, cell(1, 0), { height: 0.42, offsetX: -0.02 }),
  djHeadphones: head("DJヘッドホン", street, cell(2, 0), { height: 0.45, lift: 0.52 }),
  lightningClip: side("ライトニングクリップ", street, cell(3, 0), { height: 0.25 }),
  sportBand: head("スターヘッドバンド", street, cell(0, 1), { height: 0.24, lift: 0.52 }),
  laurel: head("勝利の月桂冠", street, cell(1, 1), { height: 0.4, lift: 0.58 }),
  headset: side("ヘッドセットマイク", street, cell(2, 1), { height: 0.35, offsetX: -0.13, offsetY: 0.08 }),
  equalizerHalo: effect("ミュージックハロー", street, cell(3, 1), { height: 0.68, lift: 0.58 }),
  roundShades: eyes("レトロサングラス", street, cell(0, 2), { height: 0.22 }),
  bucketHat: hat("バケットハット", street, cell(1, 2), { height: 0.43 }),
  comicBurst: effect("コミックバースト", street, cell(2, 2), { height: 0.82, lift: 0.44 }),
  speedLines: effect("スピードライン", street, cell(3, 2), { height: 0.7, lift: 0.34, offsetX: 0.18 }),

  kitsuneMask: side("狐面", fantasy, cell(0, 0), { height: 0.35, offsetX: -0.16 }),
  kanzashi: side("花かんざし", fantasy, cell(1, 0), { height: 0.31, offsetX: -0.17 }),
  oniHorns: head("鬼角", fantasy, cell(2, 0), { height: 0.39, lift: 0.7 }),
  fanClip: side("扇の髪飾り", fantasy, cell(3, 0), { height: 0.32, offsetX: -0.17 }),
  wizardHat: hat("星読みの帽子", fantasy, cell(0, 1), { height: 0.48, lift: 0.58 }),
  elfJewels: head("エルフジュエル", fantasy, cell(1, 1), { height: 0.37, lift: 0.42 }),
  goggles: eyes("冒険ゴーグル", fantasy, cell(2, 1), { height: 0.25 }),
  compassHalo: effect("星のコンパス", fantasy, cell(3, 1), { height: 0.72, lift: 0.48 }),
  disguise: eyes("おひげメガネ", fantasy, cell(0, 2), { height: 0.33, lift: 0.05 }),
  sweatDrops: side("あせあせ", fantasy, cell(1, 2), { height: 0.3, offsetX: -0.18 }),
  ufoHalo: effect("UFOオービット", fantasy, cell(2, 2), { height: 0.7, lift: 0.54 }),
  questionSpark: side("はてなキラリ", fantasy, cell(3, 2), { height: 0.33, offsetX: -0.18 }),

  cyberCat: head("サイバーねこ", cool, cell(0, 0), { height: 0.44, lift: 0.62 }),
  cyberBunny: head("サイバーうさぎ", cool, cell(1, 0), { height: 0.46, lift: 0.66 }),
  coolVisor: eyes("ホロバイザー", cool, cell(2, 0), { height: 0.23 }),
  silverLaurel: head("シルバーローレル", cool, cell(3, 0), { height: 0.39, lift: 0.56 }),
  midnightCrown: hat("ミッドナイトクラウン", cool, cell(0, 1), { height: 0.33, lift: 0.68 }),
  orbitHalo: effect("プラネットオービット", cool, cell(1, 1), { height: 0.68, lift: 0.5 }),
  officerBeret: hat("オフィサーベレー", cool, cell(1, 2), { height: 0.4 }),
  starChain: head("スター・チェーン", cool, cell(3, 2), { height: 0.34, lift: 0.58 }),

  flatCat: head("フラットねこ", flat, cell(0, 0), { height: 0.37 }),
  flatBunny: head("フラットうさぎ", flat, cell(1, 0), { height: 0.42 }),
  flatSun: above("サンライズ", flat, cell(2, 0), { height: 0.3 }),
  flatCrown: hat("カラーブロック冠", flat, cell(3, 0), { height: 0.32 }),
  flatBow: side("ツートーンリボン", flat, cell(0, 1), { height: 0.3 }),
  flatLightning: side("フラット稲妻", flat, cell(1, 1), { height: 0.27 }),
  flatOrbit: effect("フラットオービット", flat, cell(2, 1), { height: 0.66 }),
  flatFlower: side("カラーブロック花", flat, cell(3, 1), { height: 0.3 }),

  sketchCat: head("鉛筆ねこ", sketch, cell(0, 0), { height: 0.37 }),
  sketchBunny: head("鉛筆うさぎ", sketch, cell(1, 0), { height: 0.42 }),
  sketchCrown: hat("手描きの冠", sketch, cell(2, 0), { height: 0.32 }),
  sketchBow: side("手描きリボン", sketch, cell(3, 0), { height: 0.29 }),
  sketchHalo: above("手描きハロー", sketch, cell(0, 1), { height: 0.31 }),
  sketchHorns: head("手描きホーン", sketch, cell(1, 1), { height: 0.35 }),
  sketchStar: side("星のヘアピン", sketch, cell(2, 1), { height: 0.25 }),
  sketchDaisy: side("デイジーピン", sketch, cell(3, 1), { height: 0.25 }),

  botanicalEars: head("葉っぱのねこみみ", botanical, cell(0, 0), { height: 0.39 }),
  laceBunny: head("レースうさみみ", botanical, cell(1, 0), { height: 0.44 }),
  flowerWreath: head("ドライフラワー冠", botanical, cell(2, 0), { height: 0.4, lift: 0.58 }),
  butterfly: side("押し花バタフライ", botanical, cell(3, 0), { height: 0.31 }),
  roseBow: side("ローズリボン", botanical, cell(0, 1), { height: 0.31 }),
  fernClip: side("シダのヘアピン", botanical, cell(1, 1), { height: 0.29 }),
  shellClip: side("シェルクリップ", botanical, cell(0, 2), { height: 0.28 }),
  featherClip: side("羽根飾り", botanical, cell(2, 2), { height: 0.3 }),

  gothicCat: head("ゴシックねこ", gothic, cell(0, 0), { height: 0.42 }),
  gothicBunny: head("ゴシックうさぎ", gothic, cell(1, 0), { height: 0.44 }),
  silverHalo: above("シルバーハロー", gothic, cell(2, 0), { height: 0.33 }),
  thornHalo: effect("いばらのハロー", gothic, cell(3, 0), { height: 0.66 }),
  gothicRose: side("黒薔薇チョーカー", gothic, cell(0, 1), { height: 0.29 }),
  gothicBat: side("バットウイング", gothic, cell(1, 1), { height: 0.34, offsetX: -0.17 }),
  gothicBow: side("黒リボン", gothic, cell(2, 1), { height: 0.3 }),
  gemBrooch: side("ルビーブローチ", gothic, cell(3, 1), { height: 0.27 }),
  pixelCat: head("ドットねこ", gothic, cell(0, 2), { height: 0.36 }),
  pixelHalo: above("ドットハロー", gothic, cell(1, 2), { height: 0.31 }),
  pixelStar: side("ドットスター", gothic, cell(2, 2), { height: 0.25 }),
  pixelButterfly: side("ドットバタフライ", gothic, cell(3, 2), { height: 0.3 }),

  fitPastelHeadphones: head("パステルヘッドホン", fitted, insetCell(0, 0), { height: 0.58, lift: 0.52 }),
  fitDjHeadphones: head("ネオンDJヘッドホン", fitted, insetCell(1, 0), { height: 0.58, lift: 0.52 }),
  fitSketchHeadphones: head("手描きヘッドホン", fitted, insetCell(2, 0), { height: 0.58, lift: 0.52 }),
  fitStudioHeadset: head("スタジオヘッドセット", fitted, insetCell(3, 0), { height: 0.58, lift: 0.52 }),
  fitAngularShades: eyes("シャープサングラス", fitted, insetCell(0, 1), { height: 0.2 }),
  fitRoundGlasses: eyes("ローズ丸メガネ", fitted, insetCell(1, 1), { height: 0.24 }),
  fitCyanVisor: eyes("シアンバイザー", fitted, insetCell(2, 1), { height: 0.25 }),
  fitMonocle: eyes("モノクル＆チェーン", fitted, insetCell(3, 1), { height: 0.3, offsetX: -0.05 }),
  fitBeret: hat("フラットベレー", fitted, insetCell(0, 2), { height: 0.42 }),
  fitCrown: hat("ミニマルクラウン", fitted, insetCell(1, 2), { height: 0.34, lift: 0.72 }),
  fitLaurel: head("手描きローレル", fitted, insetCell(2, 2), { height: 0.43, lift: 0.58 }),
  fitRibbonBand: head("リボンヘッドバンド", fitted, insetCell(3, 2), { height: 0.47, lift: 0.52 }),

  fxPastelHalo: effect("パステルのきらめき", effects, insetCell(0, 0), { width: 0.53 }),
  fxCyanOrbit: effect("シアンオービット", effects, insetCell(1, 0), { width: 0.53 }),
  fxSketchBurst: effect("手描きサンバースト", effects, insetCell(2, 0), { width: 0.55 }),
  fxMangaBurst: effect("マンガ集中線", effects, insetCell(3, 0), { width: 0.56, opacity: 0.65 }),
  fxPinkWings: effect("パステルウィング", effects, insetCell(0, 1), { width: 0.63 }),
  fxCyanWings: effect("サイバーウィング", effects, insetCell(1, 1), { width: 0.63 }),
  fxLeafArc: effect("ボタニカルアーチ", effects, insetCell(2, 1), { width: 0.58 }),
  fxThornArc: effect("ゴシックソーン", effects, insetCell(3, 1), { width: 0.58 }),
  cardRibbonTrail: cardEffect("リボンの流れ星", effects, insetCell(0, 2)),
  cardTechCorners: cardEffect("テクニカルライン", effects, insetCell(1, 2), { opacity: 0.44 }),
  cardDoodleStars: cardEffect("手描きスター", effects, insetCell(2, 2), { opacity: 0.46 }),
  cardMangaStreak: cardEffect("インクスピード", effects, insetCell(3, 2), { opacity: 0.4 }),
} as const satisfies Record<string, AccessoryDefinition>;

export type AccessoryId = keyof typeof accessoryDefinitions | "none";

export const accessoryGroups: ReadonlyArray<{
  label: string;
  ids: readonly AccessoryId[];
}> = [
  { label: "けもみみ・定番", ids: ["cat", "bunny", "bear", "dog", "white", "ram", "demon", "halo", "crown", "beret", "flowers"] },
  { label: "ストリート・音楽", ids: ["pixelShades", "snapback", "lightningClip", "sportBand", "laurel", "equalizerHalo", "roundShades", "bucketHat", "comicBurst", "speedLines"] },
  { label: "和風・ファンタジー", ids: ["kitsuneMask", "kanzashi", "oniHorns", "fanClip", "wizardHat", "elfJewels", "goggles", "compassHalo", "disguise", "sweatDrops", "ufoHalo", "questionSpark"] },
  { label: "クール・サイバー", ids: ["cyberCat", "cyberBunny", "coolVisor", "silverLaurel", "midnightCrown", "orbitHalo", "officerBeret", "starChain"] },
  { label: "フラット", ids: ["flatCat", "flatBunny", "flatSun", "flatCrown", "flatBow", "flatLightning", "flatOrbit", "flatFlower"] },
  { label: "手描き", ids: ["sketchCat", "sketchBunny", "sketchCrown", "sketchBow", "sketchHalo", "sketchHorns", "sketchStar", "sketchDaisy"] },
  { label: "ボタニカル", ids: ["botanicalEars", "laceBunny", "flowerWreath", "butterfly", "roseBow", "fernClip", "shellClip", "featherClip"] },
  { label: "ゴシック・ピクセル", ids: ["gothicCat", "gothicBunny", "silverHalo", "thornHalo", "gothicRose", "gothicBat", "gothicBow", "gemBrooch", "pixelCat", "pixelHalo", "pixelStar", "pixelButterfly"] },
  { label: "ぴったり装着", ids: ["fitPastelHeadphones", "fitDjHeadphones", "fitSketchHeadphones", "fitStudioHeadset", "fitAngularShades", "fitRoundGlasses", "fitCyanVisor", "fitMonocle", "fitBeret", "fitCrown", "fitLaurel", "fitRibbonBand"] },
  { label: "背後エフェクト", ids: ["fxPastelHalo", "fxCyanOrbit", "fxSketchBurst", "fxMangaBurst", "fxPinkWings", "fxCyanWings", "fxLeafArc", "fxThornArc"] },
  { label: "カードエフェクト", ids: ["cardRibbonTrail", "cardTechCorners", "cardDoodleStars", "cardMangaStreak"] },
];

export const accessoryLabel = (id: AccessoryId) =>
  id === "none" ? "デコなし" : accessoryDefinitions[id].label;

export const accessoryGroupsForSlot = (slot: DecorationSlot) =>
  accessoryGroups
    .map((group) => ({
      ...group,
      ids: group.ids.filter((id) =>
        id !== "none" && accessoryDefinitions[id].slot === slot),
    }))
    .filter((group) => group.ids.length > 0);

export const decorationSlotLabels: Record<DecorationSlot, string> = {
  head: "頭",
  face: "顔",
  behind: "背後",
  card: "カード",
};
